import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getFarmerProfile, updateFarmerProfile, listOrchards, createOrchard, deleteOrchard } from "@/lib/api";
import { ArrowLeft, Save, Plus, X, Trash2, TreePine, MapPin, Phone, Users } from "lucide-react";

const BAHAR_OPTIONS = ["Ambia (Feb–Mar)", "Mrig (Jun–Jul)", "Hasta (Sep–Oct)"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function AdminFarmerDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [farmer, setFarmer] = useState(null);
  const [orchards, setOrchards] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showOrchard, setShowOrchard] = useState(false);
  const [orchardForm, setOrchardForm] = useState(emptyOrchard());

  const load = async () => {
    const f = await getFarmerProfile(id);
    setFarmer(f);
    setForm({
      full_name: f.full_name, phone: f.phone, alternate_phone: f.alternate_phone || "",
      village: f.village, taluka: f.taluka || "", district: f.district, state: f.state,
      fpo_name: f.fpo_name || "", notes: f.notes || "", status: f.status,
    });
    const os = await listOrchards({ farmer_id: id });
    setOrchards(os);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load farmer")); /* eslint-disable-next-line */ }, [id]);

  const saveFarmer = async (e) => {
    e.preventDefault();
    try {
      await updateFarmerProfile(id, form);
      toast.success("Saved");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const addOrchard = async (e) => {
    e.preventDefault();
    if (!orchardForm.location_village) {
      toast.error("Village is required for the orchard");
      return;
    }
    try {
      await createOrchard({
        ...orchardForm,
        farmer_id: id,
        area_acres: orchardForm.area_acres ? parseFloat(orchardForm.area_acres) : null,
        tree_count: orchardForm.tree_count ? parseInt(orchardForm.tree_count) : null,
        expected_production_mt: orchardForm.expected_production_mt ? parseFloat(orchardForm.expected_production_mt) : null,
      });
      toast.success("Orchard added");
      setShowOrchard(false);
      setOrchardForm(emptyOrchard(farmer));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add orchard");
    }
  };

  const removeOrchard = async (oid, code) => {
    if (!window.confirm(`Delete orchard ${code}?`)) return;
    await deleteOrchard(oid);
    toast.success("Orchard deleted");
    load();
  };

  if (!farmer) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-56 bg-[var(--tr-sage)] rounded" />
        <div className="h-40 bg-[var(--tr-sage)]/40 rounded-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="admin-farmer-detail" className="space-y-8">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/farmer-profiles" data-testid="fp-back" className="tr-pill-btn tr-btn-outline !min-h-[40px] !py-2 !px-4 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to farmers
        </Link>
        <span className="font-mono px-3 py-1.5 rounded-full bg-[var(--tr-deep-green)] text-white text-xs font-bold tracking-wider">
          {farmer.farmer_code}
        </span>
        <span className="px-3 py-1.5 rounded-full bg-[var(--tr-sage)] text-[var(--tr-deep-green)] text-[10px] font-bold uppercase tracking-[0.18em]">
          {farmer.status}
        </span>
      </div>

      {/* Orchard Summary (Task 6 — quick procurement overview) */}
      <div className="tr-card p-6 lg:p-8" data-testid="orchard-summary">
        <div className="flex items-center gap-2 mb-4 text-[var(--tr-deep-green)]">
          <Users className="w-5 h-5" />
          <h3 className="font-display font-extrabold text-2xl">Orchard summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <SumTile label="Farmer name" value={farmer.full_name} />
          <SumTile label="Village" value={farmer.village} />
          <SumTile label="FPO" value={farmer.fpo_name || "—"} />
          <SumTile label="Orchards" value={orchards.length} />
          <SumTile label="Total area (acres)" value={sum(orchards, "area_acres") || "—"} />
          <SumTile label="Total trees" value={sum(orchards, "tree_count") || "—"} />
          <SumTile
            label="Bahar"
            value={[...new Set(orchards.map((o) => o.bahar).filter(Boolean))].join(", ") || "—"}
          />
          <SumTile
            label="Harvest month"
            value={[...new Set(orchards.map((o) => o.harvest_month).filter(Boolean))].join(", ") || "—"}
          />
          <SumTile
            label="Expected production (MT)"
            value={sum(orchards, "expected_production_mt") || "—"}
          />
        </div>
      </div>

      {/* Farmer details (edit) */}
      <div className="tr-card p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-display font-extrabold text-xl text-[var(--tr-deep-green)]">Farmer details</h3>
          {!editing ? (
            <button data-testid="fp-edit" onClick={() => setEditing(true)} className="tr-pill-btn tr-btn-outline !min-h-[40px] !py-2 !px-4 text-sm">Edit</button>
          ) : (
            <button data-testid="fp-edit-cancel" onClick={() => setEditing(false)} className="tr-pill-btn tr-btn-outline !min-h-[40px] !py-2 !px-4 text-sm"><X className="w-4 h-4" /> Cancel</button>
          )}
        </div>

        {!editing ? (
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <Info icon={Users} label="Full name" value={farmer.full_name} />
            <Info icon={Phone} label="Mobile" value={farmer.phone} />
            <Info icon={Phone} label="Alternate mobile" value={farmer.alternate_phone || "—"} />
            <Info icon={MapPin} label="Village" value={farmer.village} />
            <Info icon={MapPin} label="Taluka" value={farmer.taluka || "—"} />
            <Info icon={MapPin} label="District" value={`${farmer.district}, ${farmer.state}`} />
            <Info icon={Users} label="FPO" value={farmer.fpo_name || "—"} />
            <Info icon={Users} label="Source" value={farmer.source} />
            {farmer.notes && <div className="md:col-span-3 text-[var(--tr-muted)] bg-[var(--tr-bone)] rounded-xl p-4"><b className="text-[var(--tr-deep-green)]">Notes:</b> {farmer.notes}</div>}
          </dl>
        ) : (
          <form data-testid="fp-edit-form" onSubmit={saveFarmer} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["full_name", "Full name*"],
              ["phone", "Mobile*"],
              ["alternate_phone", "Alternate mobile"],
              ["village", "Village*"],
              ["taluka", "Taluka"],
              ["district", "District"],
              ["state", "State"],
              ["fpo_name", "FPO name"],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="tr-label">{label}</span>
                <input data-testid={`fp-edit-${k}`} className="tr-input" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </label>
            ))}
            <label className="block md:col-span-2">
              <span className="tr-label">Notes</span>
              <textarea data-testid="fp-edit-notes" className="tr-input tr-textarea" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            <label className="block">
              <span className="tr-label">Status</span>
              <select data-testid="fp-edit-status" className="tr-input" value={form.status || "new"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {["new", "verified", "onboarded", "inactive"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <div className="md:col-span-2">
              <button data-testid="fp-edit-save" type="submit" className="tr-pill-btn tr-btn-zest"><Save className="w-4 h-4" /> Save changes</button>
            </div>
          </form>
        )}
      </div>

      {/* Orchards list + add */}
      <div className="tr-card p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[var(--tr-deep-green)]">
            <TreePine className="w-5 h-5" />
            <h3 className="font-display font-extrabold text-xl">Orchards ({orchards.length})</h3>
          </div>
          <button data-testid="fp-add-orchard" onClick={() => { setOrchardForm(emptyOrchard(farmer)); setShowOrchard((s) => !s); }} className="tr-pill-btn tr-btn-primary !min-h-[40px] !py-2 !px-4 text-sm">
            {showOrchard ? <><X className="w-4 h-4" /> {t("common.cancel")}</> : <><Plus className="w-4 h-4" /> Add orchard</>}
          </button>
        </div>

        {showOrchard && (
          <form data-testid="fp-add-orchard-form" onSubmit={addOrchard} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 border-t border-[rgba(6,78,59,0.08)] pt-6">
            <Field label="Village*"><input required data-testid="o-village" className="tr-input" value={orchardForm.location_village} onChange={(e) => setOrchardForm({ ...orchardForm, location_village: e.target.value })} /></Field>
            <Field label="Taluka"><input data-testid="o-taluka" className="tr-input" value={orchardForm.location_taluka} onChange={(e) => setOrchardForm({ ...orchardForm, location_taluka: e.target.value })} /></Field>
            <Field label="Area (acres)"><input inputMode="decimal" data-testid="o-area" className="tr-input" value={orchardForm.area_acres} onChange={(e) => setOrchardForm({ ...orchardForm, area_acres: e.target.value })} /></Field>
            <Field label="Number of trees"><input inputMode="numeric" data-testid="o-trees" className="tr-input" value={orchardForm.tree_count} onChange={(e) => setOrchardForm({ ...orchardForm, tree_count: e.target.value })} /></Field>
            <Field label="Average tree age"><input data-testid="o-age" className="tr-input" placeholder="e.g. 7 years" value={orchardForm.average_tree_age} onChange={(e) => setOrchardForm({ ...orchardForm, average_tree_age: e.target.value })} /></Field>
            <Field label="Bahar">
              <select data-testid="o-bahar" className="tr-input" value={orchardForm.bahar} onChange={(e) => setOrchardForm({ ...orchardForm, bahar: e.target.value })}>
                <option value="">—</option>
                {BAHAR_OPTIONS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Expected harvest month">
              <select data-testid="o-month" className="tr-input" value={orchardForm.harvest_month} onChange={(e) => setOrchardForm({ ...orchardForm, harvest_month: e.target.value })}>
                <option value="">—</option>
                {MONTHS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Expected production (MT)"><input inputMode="decimal" data-testid="o-production" className="tr-input" value={orchardForm.expected_production_mt} onChange={(e) => setOrchardForm({ ...orchardForm, expected_production_mt: e.target.value })} /></Field>
            <Field label="Expected price (₹/kg)"><input data-testid="o-price" className="tr-input" placeholder="e.g. 25 or 25–30" value={orchardForm.expected_selling_price_per_kg} onChange={(e) => setOrchardForm({ ...orchardForm, expected_selling_price_per_kg: e.target.value })} /></Field>
            <Field label="Irrigation type">
              <select data-testid="o-irrigation" className="tr-input" value={orchardForm.irrigation_type} onChange={(e) => setOrchardForm({ ...orchardForm, irrigation_type: e.target.value })}>
                <option value="">—</option>
                {["Drip", "Sprinkler", "Flood", "Rainfed", "Other"].map((i) => <option key={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Remarks" className="md:col-span-3"><textarea data-testid="o-remarks" className="tr-input tr-textarea" value={orchardForm.remarks} onChange={(e) => setOrchardForm({ ...orchardForm, remarks: e.target.value })} /></Field>
            <div className="md:col-span-3">
              <button data-testid="fp-add-orchard-submit" type="submit" className="tr-pill-btn tr-btn-zest">Create orchard</button>
            </div>
          </form>
        )}

        {orchards.length === 0 ? (
          <div data-testid="fp-orchards-empty" className="text-[var(--tr-muted)] py-6 text-center border border-dashed border-[rgba(6,78,59,0.2)] rounded-xl">
            No orchards yet for this farmer.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="fp-orchards-table">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tr-sage)]/40">
                <tr>
                  {["Orchard ID", "Village", "Area", "Trees", "Age", "Bahar", "Harvest", "Prod. (MT)", "Price"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.16em]">{h}</th>
                  ))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {orchards.map((o) => (
                  <tr key={o.id} data-testid={`o-row-${o.orchard_code}`} className="border-t border-[rgba(6,78,59,0.06)]">
                    <td className="px-3 py-2 font-mono font-semibold text-[var(--tr-deep-green)]">{o.orchard_code}</td>
                    <td className="px-3 py-2">{o.location_village}</td>
                    <td className="px-3 py-2">{o.area_raw || o.area_acres || "—"}</td>
                    <td className="px-3 py-2">{o.tree_count ?? "—"}</td>
                    <td className="px-3 py-2">{o.average_tree_age || "—"}</td>
                    <td className="px-3 py-2 text-xs">{o.bahar || "—"}</td>
                    <td className="px-3 py-2">{o.harvest_month || "—"}</td>
                    <td className="px-3 py-2">{o.expected_production_raw || o.expected_production_mt || "—"}</td>
                    <td className="px-3 py-2">{o.expected_selling_price_per_kg || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`o-delete-${o.orchard_code}`}
                        onClick={() => removeOrchard(o.id, o.orchard_code)}
                        className="inline-flex items-center gap-1 text-[var(--tr-zest)] hover:text-red-700 text-xs font-semibold"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function emptyOrchard(farmer = null) {
  return {
    location_village: farmer?.village || "",
    location_taluka: farmer?.taluka || "",
    area_acres: "", tree_count: "", average_tree_age: "",
    bahar: "", harvest_month: "", expected_production_mt: "",
    expected_selling_price_per_kg: "", irrigation_type: "", remarks: "",
  };
}

function sum(arr, key) {
  const total = arr.reduce((s, x) => s + (Number(x[key]) || 0), 0);
  return total > 0 ? total : "";
}

function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="tr-label">{label}</span>{children}</label>;
}

function SumTile({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--tr-muted)] font-bold">{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-[var(--tr-deep-green)] break-words">{value}</div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-full bg-[var(--tr-sage)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[var(--tr-deep-green)]" /></span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--tr-muted)] font-bold">{label}</div>
        <div className="mt-0.5 font-semibold text-[var(--tr-deep-green)] break-words">{value}</div>
      </div>
    </div>
  );
}
